import { create } from "zustand";
import type { CNSShare, CNSShareEntityType } from "@/models/cns";
import { fetchSharesForCurrentUser as fetchSharesFromApi } from "@/services/shareService";
import { getErrorMessage } from "@/types/problem-detail";

type ShareRole = "editor" | "viewer" | "member";

/** Coalesce concurrent share list fetches (dashboard layout + StrictMode). No TTL. */
let pendingSharesForCurrentUser: Promise<void> | null = null;

interface CurrentUserSharesStore {
  shares: CNSShare[];
  loading: boolean;
  error: string | null;
  isInitialized: boolean;
  fetchSharesForCurrentUser: () => Promise<void>;
  getShareForEntity: (entityType: CNSShareEntityType, entityPublicId: string) => CNSShare | undefined;
  isShared: (entityType: CNSShareEntityType, entityPublicId: string) => boolean;
  roleForEntity: (entityType: CNSShareEntityType, entityPublicId: string) => ShareRole | null;
  clearAll: () => void;
}

export const useCurrentUserSharesStore = create<CurrentUserSharesStore>((set, get) => ({
  shares: [],
  loading: false,
  error: null,
  isInitialized: false,

  fetchSharesForCurrentUser: async () => {
    if (pendingSharesForCurrentUser) return pendingSharesForCurrentUser;
    pendingSharesForCurrentUser = (async () => {
      set({ loading: true, error: null });
      try {
        const shares = await fetchSharesFromApi();
        set({ shares, loading: false, error: null, isInitialized: true });
      } catch (err) {
        set({
          error: getErrorMessage(err, "Failed to load shares"),
          loading: false,
        });
      } finally {
        pendingSharesForCurrentUser = null;
      }
    })();
    return pendingSharesForCurrentUser;
  },

  getShareForEntity: (entityType, entityPublicId) => {
    return get().shares.find(
      (s) => s.entityType === entityType && s.entityPublicId === entityPublicId
    );
  },

  isShared: (entityType, entityPublicId) => {
    return get().getShareForEntity(entityType, entityPublicId) !== undefined;
  },

  roleForEntity: (entityType, entityPublicId) => {
    const share = get().getShareForEntity(entityType, entityPublicId);
    return share ? (share.role as ShareRole) : null;
  },

  clearAll: () => set({ shares: [], isInitialized: false, error: null }),
}));
